import React from 'react';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { TableBlock, TextBlock, TestBlock } from '@/types/report-payload';
import { AISectionSkeleton } from './AISectionSkeleton';
import EditableSection from './EditableSection';
import { isSplitBlockId, stripSplitBlockId } from './pagination/utils';
import { ReportData } from './types';

interface TestTextProps {
    block: TextBlock;
    disabled?: boolean;
    onBlockUpdate: (blockId: string | number, updates: Partial<TestBlock>) => void;
    onDelete?: () => void;
    isEditing?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
    isAfterTable?: boolean;
    previousTableBlock?: TableBlock;
    reportData: ReportData;
}

const TestText: React.FC<TestTextProps> = ({
    block,
    disabled = false,
    onBlockUpdate,
    onDelete,
    isEditing,
    onEditingChange,
    previousTableBlock,
    reportData,
}) => {
    const { generatingTestBlockId, generationPhase } = useReportWizard();
    const [localEditing, setLocalEditing] = React.useState(false);
    const resolvedEditing = isEditing ?? localEditing;
    const handleEditingChange = onEditingChange || setLocalEditing;
    const blockIdentifier = block.id || block.order;
    const normalizedBlockId = stripSplitBlockId(blockIdentifier);
    const isGeneratingThisBlock = generatingTestBlockId === normalizedBlockId;
    const isLinkedTextBlock = !!block.linked_table_id;
    const isSplitContinuation = isSplitBlockId(block.id);
    const shouldHideOrphanLinkedTextBlock =
        isLinkedTextBlock && !previousTableBlock && !isSplitContinuation;
    const shouldHideLinkedTextBlock =
        isLinkedTextBlock && !shouldHideOrphanLinkedTextBlock && !block.content?.trim();

    const fullBlockContent = React.useMemo(() => {
        const allBlocks = reportData.tests?.blocks || [];
        const originalBlock = allBlocks.find((currentBlock) => {
            if (typeof blockIdentifier === 'string') {
                return stripSplitBlockId(currentBlock.id) === normalizedBlockId;
            }

            return !currentBlock.id && currentBlock.order === blockIdentifier;
        }) as TextBlock | undefined;

        return originalBlock?.content ?? block.content;
    }, [block.content, blockIdentifier, normalizedBlockId, reportData.tests?.blocks]);

    if (isGeneratingThisBlock) {
        return <AISectionSkeleton visible sectionId="tests" phase={generationPhase} />;
    }

    if (shouldHideOrphanLinkedTextBlock || shouldHideLinkedTextBlock) {
        return null;
    }

    return (
        <div className="group/text relative">
            <EditableSection
                content={block.content}
                defaultEditValue={fullBlockContent}
                copyContent={fullBlockContent}
                onSave={(val) => onBlockUpdate(blockIdentifier, { content: val })}
                onDelete={onDelete}
                placeholder="Clique para adicionar texto..."
                disabled={disabled}
                isEditing={resolvedEditing}
                onEditingChange={handleEditingChange}
                reportId={reportData.id}
                sectionId="tests"
                anchorId={`tests-${normalizedBlockId}`}
            />
        </div>
    );
};

export default TestText;
